import React from 'react'
import { ItemRenderProps } from '@components/UniversalAPISelector'
import { collectFromPages, fetchBusinesses, IBusiness, PaginatedResponse } from '@root/query'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { InputProps } from '@components'
import styled from 'styled-components'
import { FaBuilding, FaCheck } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import UniversalAPISelector from '@root/components/UniversalAPISelector/UniversalAPISelector'

export type IBusinessOption = Pick<IBusiness, 'id' | 'slug' | 'name'>

const LIST_QUERY_KEY = ['businesses', 'selector', 'list']

interface Props extends Omit<InputProps, 'value' | 'onChange'> {
  value: IBusinessOption | null
  onChange: (business: IBusinessOption | null) => void
}

export const BusinessSelector: React.FC<Props> = ({ value, onChange, ...rest }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // The clicked row was rendered from the cached list, so the cache always holds it
  const findBusiness = (id: number): IBusinessOption | null => {
    const match = queryClient
      .getQueriesData<InfiniteData<PaginatedResponse<IBusiness>>>({ queryKey: LIST_QUERY_KEY })
      .flatMap(([, data]) => collectFromPages(data))
      .find(business => business.id === id)
    return match ? { id: match.id, slug: match.slug, name: match.name } : null
  }

  const renderRow = (
    item: IBusiness,
    { isFocused, isSelected, onKeyDown, handleOnItemClick, style }: ItemRenderProps<IBusiness>
  ) => (
    <ItemWrapper
      className={isFocused && 'keyboard-focused'}
      onKeyDown={onKeyDown}
      onClick={() => handleOnItemClick(item)}
      style={style}
      key={item.id}
    >
      <Item title={item.name}>
        <ItemColumn>{item.name}</ItemColumn>
        <RightItemsWrapper>
          {isSelected && (
            <ItemColumn>
              <CheckMarkWrapper>
                <FaCheck />
              </CheckMarkWrapper>
            </ItemColumn>
          )}
        </RightItemsWrapper>
      </Item>
    </ItemWrapper>
  )

  return (
    <UniversalAPISelector
      {...rest}
      value={value?.id}
      onChange={(id: number | null) => onChange(id == null ? null : findBusiness(id))}
      getItemQueryKeyFn={id => ['businesses', 'selector', 'item', id]}
      getItem={async id => (value?.id === id ? value : null)}
      getItemsQueryKeyFn={params => [...LIST_QUERY_KEY, params]}
      getItems={({ search, page }) => fetchBusinesses(undefined, { search, page })}
      renderRow={renderRow}
      noDataMessage={t('notificationFeed.preferences.businessNoResults')}
      dropdownHeaderIcon={<FaBuilding />}
      dropDownHeader={t('notificationFeed.preferences.businessLabel')}
      placeholder={t('notificationFeed.preferences.businessPlaceholder')}
      getDataToShow={(item: IBusinessOption) => item.name}
    />
  )
}

const Item = styled(motion.div).attrs({
  variants: {
    hidden: { y: 5, opacity: 0 },
    show: { y: 0, opacity: 1 }
  }
})`
  display: flex;
  width: 100%;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  height: 35px;
`

const ItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const RightItemsWrapper = styled.div`
  margin-left: auto;
  display: flex;
`

const ItemColumn = styled.div`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin: ${({ theme }) => theme.spacing.sm}rem;
  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`
